import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { CopyButton } from '@resource-kit/ui/react';
import { EXTERNAL_URLS } from '@/constants/config';

/**
 * A displayed address as a Google Maps search link with a copy button beside
 * it (#2689). Host-side on purpose: Google Maps is this app's business
 * knowledge, not a resource-kit concern.
 *
 * The caller guarantees a non-empty `address`; each widget keeps its own empty
 * display. The copy button is a SIBLING of the link, never inside it, and both
 * stop propagation so a click does not also fire a table row's click handler.
 * Below `md` (the same 768px line `useIsPhone` uses) the link and the button
 * grow to a 44px touch target.
 */
export function AddressLink({ address }: { address: string }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex max-w-full items-center gap-1">
      <a
        href={EXTERNAL_URLS.googleMapsSearch(address)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e: MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
        className="min-w-0 text-sm text-primary underline-offset-2 hover:underline max-md:inline-flex max-md:min-h-11 max-md:items-center"
      >
        {address}
        <ExternalLink aria-hidden className="ml-1 inline size-3 shrink-0" />
        <span className="sr-only">{t('address.opensMaps')}</span>
      </a>
      <CopyButton
        value={address}
        label={t('address.copy')}
        successMessage={t('address.copied')}
        className="max-md:inline-flex max-md:size-11 max-md:items-center max-md:justify-center"
      />
    </span>
  );
}
